'use client';

import { format } from "date-fns";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { ChevronLeft, ChevronRight, SearchIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { createClient } from "@/utils/supabase/client";
import { renderPageButtons } from "./pagination";
import { useToast } from "@/hooks/use-toast";
  
const perPageOptions = [10, 20, 50, 100];

export default function Table({
  headerBackground,
  campaign,
}: {
  headerBackground: string;
  campaign: any;
}) {
  const { toast } = useToast();
  const [search, setSearch] = useState<string>("");
  const [perPage, setPerPage] = useState<number>(perPageOptions?.[0]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [participants, setParticipants] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);

  const getParticipants = async () => {
    const supabase = await createClient();
    const { data, error } = await supabase.rpc("get_participants_masked", {
      p_campaign_id: campaign.id,
      p_search: search || null,
      p_limit: perPage,
      p_offset: (currentPage - 1) * perPage,
    });

    if (error) {
      toast({
        variant: "destructive",
        title: "Rất tiếc",
        description: "Chưa có danh sách người tham gia",
      })
      return;
    }
    if (data?.total_count > 0) {
      setParticipants(data?.data);
      setTotalCount(Number(data.total_count));
    } else {
      setParticipants([]);
      setTotalCount(0);
    }
  };

  useEffect(() => {
    getParticipants();
  }, [campaign, perPage, currentPage]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-4 justify-center">
        <div className="flex items-center gap-1 w-[375px] relative">
          <Input className="bg-white h-12 pr-12" placeholder="Tìm kiếm" value={search} onChange={(e) => setSearch(e.target.value)} />
          <div onClick={() => getParticipants()} className="cursor-pointer absolute right-4 top-1/2 -translate-y-1/2">
            <SearchIcon className="w-6 h-6" />
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <div className="overflow-x-auto -m-1">
          <table className="w-full text-md border-separate border-spacing-y-1 border-spacing-x-1">
            {/* Header */}
            <thead>
              <tr className="text-white text-center rounded-sm" style={{ backgroundColor: headerBackground }}>
                <th className="p-4 font-semibold rounded-sm w-[60px]">STT</th>
                <th className="p-4 font-semibold rounded-sm">Tên Khách hàng</th>
                <th className="p-4 font-semibold rounded-sm">Ngày tham gia</th>
                <th className="p-4 font-semibold rounded-sm">Số điện thoại</th>
              </tr>
            </thead>

            {/* Body */}
            <tbody>
              {
                participants?.length === 0 ? (
                  <tr key="no-data-table">
                    <td colSpan={4} className="text-center p-20 text-red-500 font-semibold">Không có dữ liệu</td>
                  </tr>
                ) : (
                  participants?.map((row: any, index: number) => (
                    <tr key={row.id} className="text-center rounded-sm">
                      <td className="p-4 rounded-sm bg-gray-100 w-[60px]">{index + 1}</td>
                      <td className="p-4 rounded-sm bg-gray-100">{row.name}</td>
                      <td className="p-4 rounded-sm bg-gray-100">{format(row.date, "dd/MM/yyyy")}</td>
                      <td className="p-4 rounded-sm bg-gray-100">{row.phone}</td>
                    </tr>
                  ))
                )
              }
            </tbody>
          </table>
        </div>
        <div className="px-3 py-3 border rounded-sm w-full flex items-center justify-between bg-white">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft />
              </Button>

              {renderPageButtons(totalCount, perPage, currentPage, setCurrentPage)}

              <Button
                variant="ghost"
                size="icon"
                onClick={() =>
                  setCurrentPage(Math.min(currentPage + 1, Math.ceil(totalCount / perPage)))
                }
                disabled={currentPage === Math.ceil(totalCount / perPage)}
              >
                <ChevronRight />
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Select
                value={perPage.toString()}
                onValueChange={(value) => setPerPage(parseInt(value))}
              >
                <SelectTrigger className="bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {perPageOptions?.map((item: any, index: number) => (
                      <SelectItem
                        key={index}
                        value={item.toString()}
                      >
                        {item}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              <span className="text-sm">{perPage} dòng trên mỗi trang</span>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm">
            Tổng {totalCount} dòng
          </div>
        </div>
      </div>
    </div>
  )
}
